import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Users, Video, Archive, MessageCircle, Star, Ban } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { MaskedLines, Reveal, SectionHead } from '../components/motion';
import LeadModal from '../components/LeadModal';
import Countdown from '../components/Countdown';

const IMG_COMPANY = '/images/office-wall.jpg';
const LAUNCH_DATE = new Date('2027-01-18T09:00:00-05:00').getTime();

function LaunchCountdown() {
  const { t } = useLang();
  const [left, setLeft] = useState(Math.max(0, LAUNCH_DATE - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, LAUNCH_DATE - Date.now())), 1000);
    return () => clearInterval(id);
  }, []);
  const units = [
    { v: Math.floor(left / 86400000), l: t('training.countdownUnits.d') },
    { v: Math.floor((left % 86400000) / 3600000), l: t('training.countdownUnits.h') },
    { v: Math.floor((left % 3600000) / 60000), l: t('training.countdownUnits.m') },
    { v: Math.floor((left % 60000) / 1000), l: t('training.countdownUnits.s') },
  ];
  return (
    <div data-testid="training-countdown" className="inline-block">
      <p className="text-xs font-semibold tracking-widest text-blue-400 mb-3">{t('training.countdownTitle')}</p>
      <div className="flex flex-wrap justify-center gap-2.5">
        {units.map((u, i) => (
          <div key={i} className="rounded-xl border border-slate-700/70 bg-[#1E293B] px-4 py-3 text-center min-w-[72px]">
            <p className="font-display text-3xl sm:text-4xl text-white tabular-nums">{String(u.v).padStart(2, '0')}</p>
            <p className="text-[9px] font-semibold tracking-widest text-slate-500 mt-1">{u.l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Training() {
  const { t } = useLang();
  const [leadOpen, setLeadOpen] = useState(false);
  const [tier, setTier] = useState('');

  const stats = t('training.stats');
  const components = t('training.components');
  const principles = t('training.principles');
  const months = t('training.months');
  const formatItems = t('training.formatItems');
  const commItems = t('training.commItems');
  const results = t('training.results');
  const tiers = t('training.tiers');
  const outcomes = t('training.outcomes');
  const aboutStats = t('home.aboutStats');

  const [waitlist, setWaitlist] = useState(false);

  const openLead = (tierName = '', isWaitlist = false) => {
    setTier(tierName);
    setWaitlist(isWaitlist);
    setLeadOpen(true);
  };

  return (
    <main data-testid="training-page">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-blue-600/15 blur-[140px] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 w-full text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-2 mb-8"
            data-testid="training-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 pulse-dot" />
            <span className="text-xs font-semibold text-green-300">{t('training.badge')}</span>
          </motion.div>
          <MaskedLines
            lines={t('training.heroLines')}
            colors={['text-white', 'text-blue-400', 'text-green-400']}
            className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05]"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-2xl mx-auto text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="training-hero-sub"
          >
            {t('training.heroSub')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap justify-center gap-4"
          >
            <button
              data-testid="training-consult-btn"
              onClick={() => openLead()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-7 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-400 hover:shadow-[0_0_35px_rgba(59,130,246,0.5)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('training.ctaPrimary')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <a
              data-testid="training-formats-btn"
              href="#formats"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-[#1E293B] px-7 py-4 text-base font-semibold text-slate-200 transition-all duration-300 hover:border-blue-500/60 hover:text-blue-300 hover:-translate-y-0.5"
            >
              {t('training.ctaSecondary')}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-12"
          >
            <LaunchCountdown />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto"
          >
            {stats.map((s, i) => (
              <div key={i} data-testid={`training-stat-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] p-4">
                <p className="font-display text-2xl text-white">{s.v}</p>
                <p className="text-[10px] font-semibold tracking-widest text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="approach-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <SectionHead overline={t('training.approachOverline')} title="" className="mb-8" />
          <Reveal>
            <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-white border-l-4 border-blue-500 pl-8" data-testid="approach-quote">
              {t('training.approachQuote')}
            </blockquote>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 text-slate-400 leading-relaxed">{t('training.approachText1')}</p>
            <p className="mt-4 text-slate-300 leading-relaxed">{t('training.approachText2')}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="components-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('training.compOverline')} title={t('training.compTitle')} align="center" className="max-w-3xl mx-auto" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {components.map((c, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`component-card-${i}`} className="h-full rounded-xl border border-slate-700/70 bg-[#1E293B] p-7 text-center transition-all duration-300 hover:border-blue-500/60 hover:-translate-y-1">
                  <span className="font-display text-3xl text-blue-500/40">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-xl text-white">{c.t}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('training.princOverline')} title={t('training.princTitle')} align="center" className="max-w-3xl mx-auto" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5">
            {principles.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div data-testid={`principle-${i}`} className="flex items-start gap-4 border-b border-slate-800 pb-5">
                  <span className="font-display text-2xl text-blue-400/60">0{i + 1}</span>
                  <p className="text-sm sm:text-base text-slate-300 pt-1">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="roadmap-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('training.roadOverline')} title={t('training.roadTitle')} align="center" />
          <Reveal delay={0.1}>
            <p className="mt-4 text-xs font-semibold tracking-widest text-slate-500 text-center">{t('training.roadSub')}</p>
          </Reveal>
          <div className="mt-14 space-y-6">
            {months.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1}>
                <div data-testid={`roadmap-month-${i}`} className="rounded-xl border border-slate-700/70 bg-[#1E293B] overflow-hidden transition-colors duration-300 hover:border-blue-500/50">
                  <div className="grid grid-cols-1 lg:grid-cols-12">
                    <div className="lg:col-span-4 p-8 lg:border-r border-slate-700/60 bg-[#182540]">
                      <span className="font-display text-7xl text-blue-500/25">{m.num}</span>
                      <h3 className="mt-3 font-display text-2xl text-white">{m.title}</h3>
                      <p className="mt-3 text-[11px] font-semibold tracking-widest text-slate-500 leading-relaxed">{m.meta}</p>
                    </div>
                    <div className="lg:col-span-8 p-8">
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                        {m.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <span className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-blue-400/70" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 rounded-lg border border-green-500/25 bg-green-500/5 px-5 py-4">
                        <p className="text-[10px] font-semibold tracking-widest text-green-400 mb-1">{t('common.result')}</p>
                        <p className="text-sm text-slate-200">{m.result}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="format-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <SectionHead overline={t('training.formatOverline')} title={t('training.formatTitle')} />
            <ul className="mt-10 space-y-4">
              {formatItems.map((f, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex items-start gap-3 text-slate-300">
                    <Video className="w-4 h-4 mt-1 shrink-0 text-blue-400" />
                    <span className="text-sm sm:text-base">{f}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionHead overline={t('training.commOverline')} title={t('training.commTitle')} />
            <ul className="mt-10 space-y-4">
              {commItems.map((c, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex items-start gap-3 text-slate-300">
                    <MessageCircle className="w-4 h-4 mt-1 shrink-0 text-green-400" />
                    <span className="text-sm sm:text-base">{c}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.3}>
              <div className="mt-10 rounded-xl border border-slate-700/70 bg-[#1E293B] p-6">
                <p className="text-[10px] font-semibold tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <Archive className="w-3.5 h-3.5" /> {t('training.resOverline')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {results.map((r, i) => (
                    <span key={i} data-testid={`result-chip-${i}`} className="rounded-full border border-blue-500/30 bg-blue-500/10 px-3.5 py-1.5 text-xs font-medium text-blue-200">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="formats" className="py-24 sm:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('training.priceOverline')} title={t('training.priceTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {tiers.map((tierItem, i) => (
              <Reveal key={i} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`pricing-tier-${i}`}
                  className={`relative h-full flex flex-col rounded-xl border p-8 transition-all duration-300 hover:-translate-y-1.5 ${
                    tierItem.soldOut
                      ? 'border-slate-700/50 bg-[#1E293B]/60 opacity-80'
                      : tierItem.badge
                      ? 'border-blue-500/70 bg-[#1E293B] shadow-[0_0_45px_rgba(59,130,246,0.15)]'
                      : 'border-slate-700/70 bg-[#1E293B] hover:border-slate-500'
                  }`}
                >
                  {tierItem.soldOut ? (
                    <span data-testid={`pricing-soldout-${i}`} className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 text-[10px] font-bold tracking-widest text-white uppercase">
                      <Ban className="w-3 h-3" />
                      {t('common.soldOut')}
                    </span>
                  ) : tierItem.badge && (
                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3 py-1 text-[10px] font-bold tracking-widest text-white">
                      <Star className="w-3 h-3" />
                      {t('common.popular')}
                    </span>
                  )}
                  <h3 className={`font-display text-xl ${tierItem.soldOut ? 'text-slate-400' : 'text-white'}`}>{tierItem.name}</h3>
                  <p className={`mt-4 font-display text-5xl ${tierItem.soldOut ? 'text-slate-500 line-through decoration-red-500/70 decoration-2' : 'text-white'}`}>{tierItem.price}</p>
                  <p className="mt-2 text-[11px] font-semibold tracking-widest text-slate-500 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" /> {tierItem.size} · {tierItem.start}
                  </p>
                  {tierItem.startDate && !tierItem.soldOut && (
                    <Countdown target={tierItem.startDate} testId={`pricing-countdown-${i}`} />
                  )}
                  <ul className="mt-7 space-y-3 flex-1">
                    {tierItem.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-xs text-slate-500 leading-relaxed">{tierItem.soldOut ? t('common.soldOutHint') : tierItem.fit}</p>
                  <button
                    data-testid={tierItem.soldOut ? `pricing-waitlist-${i}` : `pricing-choose-${i}`}
                    onClick={() => openLead(tierItem.name, !!tierItem.soldOut)}
                    className={`mt-7 w-full rounded-lg px-6 py-3.5 text-base font-semibold transition-all duration-300 active:scale-[0.98] ${
                      tierItem.soldOut
                        ? 'border border-amber-500/50 text-amber-300 hover:border-amber-400 hover:bg-amber-500/10'
                        : tierItem.badge
                        ? 'bg-blue-500 text-white hover:bg-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.45)]'
                        : 'border border-slate-600 text-slate-100 hover:border-blue-500/60 hover:text-blue-300'
                    }`}
                  >
                    {tierItem.soldOut ? t('common.waitlistBtn') : t('common.choose')}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-[#111D36] border-y border-slate-800" data-testid="mentor-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-xl border border-blue-500/20 pointer-events-none" />
              <img src={IMG_COMPANY} alt={t('home.aboutName')} className="w-full h-[420px] object-cover rounded-xl border border-slate-700/70" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <SectionHead overline={t('training.aboutOverline')} title={t('home.aboutName')} />
            <p className="mt-3 text-xs font-semibold tracking-widest text-green-400">{t('home.aboutRole')}</p>
            <Reveal delay={0.1}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">{t('home.aboutBio')}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {aboutStats.map((s, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div>
                    <p className="font-display text-3xl sm:text-4xl text-blue-400">{s.v}</p>
                    <p className="text-[10px] font-semibold tracking-widest text-slate-500 mt-1">{s.l}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="outcomes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead overline={t('training.outOverline')} title={t('training.outTitle')} align="center" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {outcomes.map((o, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`outcome-${i}`} className="flex items-start gap-3 rounded-xl border border-slate-700/70 bg-[#1E293B] p-5 h-full transition-colors duration-300 hover:border-green-500/50">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-green-400" />
                  <p className="text-sm text-slate-300">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 relative overflow-hidden" data-testid="training-cta">
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05]">{t('training.ctaTitle')}</h2>
            <p className="mt-5 text-blue-100">{t('training.ctaSub')}</p>
            <button
              data-testid="training-cta-apply-btn"
              onClick={() => openLead()}
              className="mt-9 inline-flex items-center gap-2 rounded-lg bg-white px-9 py-4 text-lg font-semibold text-blue-700 transition-all duration-300 hover:bg-blue-50 hover:shadow-[0_0_45px_rgba(255,255,255,0.35)] hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {t('training.ctaBtn')}
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-5 text-xs font-medium text-blue-200">{t('training.ctaNote')}</p>
          </Reveal>
        </div>
      </section>

      <LeadModal
        open={leadOpen}
        onOpenChange={setLeadOpen}
        waitlist={waitlist}
        source={waitlist ? `waitlist: ${tier}` : tier ? `training: ${tier}` : 'training'}
        onSuccess={waitlist ? undefined : () => { window.location.href = '/training-payment'; }}
      />
    </main>
  );
}
